import Icon from "@ch/components/Icons/Icon";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import useAudioRecorder from "../hooks/useAudioRecorder";

interface Props {
  onSend: (text: string) => void;
  onSendVoice?: (audio: Blob) => void;
  disabled?: boolean;
  placeholder?: string;
}

const KemChatInput: FC<Props> = ({
  onSend,
  onSendVoice,
  disabled,
  placeholder = "Type a message",
}) => {
  const [value, setValue] = useState("");
  const {
    isRecording,
    isPaused,
    durationLabel,
    audioBlob,
    start,
    stop,
    pause,
    resume,
    restart,
    discardRecording,
    cancelRecording,
  } = useAudioRecorder();

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(() => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    setValue("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [disabled, onSend, value]);

  // const handleSendVoice = useCallback(() => {
  //   if (!audioBlob || disabled) return;
  //   onSendVoice?.(audioBlob);
  //   // discardRecording handles preview cleanup + reset
  //   discardRecording();
  // }, [audioBlob, disabled, discardRecording, onSendVoice]);

  const hasVoiceControls = isRecording || !!audioBlob;

  // Auto-send voice once recording stops and we have a blob
  const hasAutoSentRef = useRef(false);
  useEffect(() => {
    if (!isRecording && audioBlob && !disabled && !hasAutoSentRef.current) {
      hasAutoSentRef.current = true;
      onSendVoice?.(audioBlob);
      // cleanup preview + reset
      discardRecording();
    }
  }, [audioBlob, disabled, discardRecording, isRecording, onSendVoice]);

  // Reset the auto-send guard when a new recording starts
  useEffect(() => {
    if (isRecording) hasAutoSentRef.current = false;
  }, [isRecording]);

  return (
    <div
      className={`flex min-h-14 w-full min-w-0 items-center rounded-2xl border border-orange bg-white p-2 transition-shadow focus-within:ring-2 focus-within:ring-orange/25 sm:p-3 ${
        disabled ? "opacity-60" : ""
      }`}
    >
      <textarea
        className={`kem-tour-text-input min-w-0 resize-none overflow-hidden p-0 ${
          hasVoiceControls
            ? "w-[45%] border-none outline-none sm:w-[55%] md:flex-1"
            : "flex-1 border-none outline-none"
        }`}
        disabled={disabled}
        onChange={(e) => {
          setValue(e.target.value);
          // Auto-resize
          e.target.style.height = "auto";
          e.target.style.height = `${e.target.scrollHeight}px`;
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
        }}
        placeholder={placeholder}
        ref={textareaRef}
        rows={1}
        value={value}
      />
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-2 gap-y-1 sm:gap-x-3">
        {/* Record/Voice controls */}
        {!isRecording && !audioBlob && (
          <button
            aria-label="Start recording"
            className="kem-tour-voice-button text-orange disabled:opacity-50"
            disabled={disabled}
            onClick={() => void start()}
            type="button"
          >
            <Icon name="microphone" size={24} />
          </button>
        )}

        {isRecording && !isPaused && (
          <>
            <span
              aria-live="polite"
              className="text-[10px] text-ink-700 md:text-xs"
            >
              Recording {durationLabel}
            </span>
            <button
              className="text-[10px] text-ink-700 md:text-xs"
              onClick={() => void stop()}
              type="button"
            >
              Stop
            </button>
            <button
              className="text-[10px] text-ink-700 md:text-xs"
              onClick={() => void pause()}
              type="button"
            >
              Pause
            </button>
            <button
              className="text-[10px] text-ink-700 underline md:text-xs"
              onClick={() => void cancelRecording()}
              type="button"
            >
              Cancel
            </button>
          </>
        )}

        {isRecording && isPaused && (
          <>
            <span
              aria-live="polite"
              className="text-[10px] text-ink-700 md:text-xs"
            >
              Paused {durationLabel}
            </span>
            <button
              className="text-[10px] text-ink-700 md:text-xs"
              onClick={() => void resume()}
              type="button"
            >
              Resume
            </button>
            <button
              className="text-[10px] text-ink-700 md:text-xs"
              onClick={() => void restart()}
              type="button"
            >
              Restart
            </button>
            <button
              className="text-[10px] text-ink-700 underline md:text-xs"
              onClick={() => void cancelRecording()}
              type="button"
            >
              Cancel
            </button>
          </>
        )}

        {!isRecording && audioBlob && (
          <div className="flex items-center gap-2">
            {/**
            <button
              type="button"
              onClick={handleSendVoice}
              className="text-[10px] text-primary md:text-xs"
            >
              Send
            </button>
            */}
            <button
              className="text-[10px] text-orange md:text-xs"
              onClick={() => discardRecording()}
              type="button"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Text send */}
        <button
          className="kem-tour-send-button text-orange disabled:opacity-50"
          disabled={disabled ?? isRecording}
          onClick={handleSubmit}
          type="button"
        >
          <Icon name="send" size={24} />
        </button>
      </div>
    </div>
  );
};

export default KemChatInput;
